import type { ApplicationAudio } from "../audio/applicationAudio"
import { buttonElement, canvasElement, htmlElement } from "./dom"

type AudioProvider = () => ApplicationAudio

class CanvasContextError extends Error {
  constructor() {
    super("Spectral sketch canvas is unavailable")
    this.name = "CanvasContextError"
  }
}

function drawingContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const context = canvas.getContext("2d", { willReadFrequently: true })
  if (context === null) throw new CanvasContextError()
  return context
}

export function bindSonifier(provider: AudioProvider): void {
  const canvas = canvasElement("draw-canvas")
  const context = drawingContext(canvas)
  context.fillStyle = "#05070a"
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.strokeStyle = "#58a6ff"
  context.lineWidth = 5
  context.lineCap = "round"
  let drawing = false

  const position = (event: PointerEvent): readonly [number, number] => {
    const bounds = canvas.getBoundingClientRect()
    return [
      ((event.clientX - bounds.left) * canvas.width) / bounds.width,
      ((event.clientY - bounds.top) * canvas.height) / bounds.height,
    ]
  }

  canvas.addEventListener("pointerdown", (event) => {
    drawing = true
    canvas.setPointerCapture(event.pointerId)
    const [x, y] = position(event)
    context.beginPath()
    context.moveTo(x, y)
  })
  canvas.addEventListener("pointermove", (event) => {
    if (!drawing) return
    const [x, y] = position(event)
    context.lineTo(x, y)
    context.stroke()
  })
  canvas.addEventListener("pointerup", () => {
    drawing = false
  })
  buttonElement("clear-drawing").addEventListener("click", () => {
    context.fillStyle = "#05070a"
    context.fillRect(0, 0, canvas.width, canvas.height)
  })
  buttonElement("play-drawing").addEventListener("click", () => {
    const app = provider()
    void app.resume().then(() => {
      const durationSeconds = 2
      const image = context.getImageData(0, 0, canvas.width, canvas.height)
      const buffer = app.context.createBuffer(
        1,
        app.context.sampleRate * durationSeconds,
        app.context.sampleRate,
      )
      const output = buffer.getChannelData(0)
      for (let y = 0; y < image.height; y += 6) {
        const hertz = 6_000 * (100 / 6_000) ** (y / image.height)
        for (let x = 0; x < image.width; x += 4) {
          const pixelIndex = (y * image.width + x) * 4
          const brightness =
            ((image.data[pixelIndex] ?? 0) +
              (image.data[pixelIndex + 1] ?? 0) +
              (image.data[pixelIndex + 2] ?? 0)) /
            765
          if (brightness <= 0.12) continue
          const start = Math.floor((x / image.width) * output.length)
          const span = Math.floor((app.context.sampleRate * durationSeconds * 4) / image.width)
          for (let sample = 0; sample < span && start + sample < output.length; sample += 1) {
            output[start + sample] =
              (output[start + sample] ?? 0) +
              Math.sin((2 * Math.PI * hertz * (start + sample)) / app.context.sampleRate) *
                brightness *
                0.01
          }
        }
      }
      let peak = 0
      for (const sample of output) peak = Math.max(peak, Math.abs(sample))
      const scale = peak > 0.28 ? 0.28 / peak : 1
      const fadeSamples = Math.floor(app.context.sampleRate * 0.02)
      for (let index = 0; index < output.length; index += 1) {
        const envelope = Math.min(1, index / fadeSamples, (output.length - 1 - index) / fadeSamples)
        output[index] = (output[index] ?? 0) * scale * Math.max(0, envelope)
      }
      const source = app.context.createBufferSource()
      source.buffer = buffer
      source.connect(app.router.input)
      source.start()
      const status = htmlElement("global-status")
      status.className = "status status-success"
      status.textContent = "Playing 2-second spectral sketch through selected output."
    })
  })
}
